import type { FeatureCollection, MultiPolygon, Point } from 'geojson';

import {
  ResultsMapColorMode,
  ResultsMapFeatureColorLegend,
  ResultsMapGeoJson,
  ResultsMapLegendEntry,
  ParsedResultsMapData,
} from '../data-access/types/results-map';

import { GesStatusValue, getGesStatusHexColor, getGesStatusMeta } from './ges-status';

function parseLegendEntry(value: unknown): ResultsMapLegendEntry | null {
  if (!value || typeof value !== 'object') return null;
  const entry = value as Record<string, unknown>;
  if (typeof entry.color !== 'string') return null;
  return {
    color: entry.color,
    value: entry.value != null ? String(entry.value) : '',
  };
}

function parseLegendEntryList(value: unknown): ResultsMapLegendEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => parseLegendEntry(item))
    .filter((item): item is ResultsMapLegendEntry => item != null);
}

function firstLegendList(...sources: unknown[]): ResultsMapLegendEntry[] {
  for (const source of sources) {
    const entries = parseLegendEntryList(source);
    if (entries.length > 0) return entries;
  }
  return [];
}

export function parseFeatureColorLegend(
  props: Record<string, unknown>,
): ResultsMapFeatureColorLegend | null {
  let raw = props.color_legend;

  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }

  if (raw && typeof raw === 'object') {
    const parsed = raw as Record<string, unknown>;
    return {
      carbon_legend: parseLegendEntry(parsed.carbon_legend) ?? undefined,
      ges_status_legend: parseLegendEntry(parsed.ges_status_legend) ?? undefined,
    };
  }

  const carbon = parseLegendEntry(props.carbon_legend);
  const ges = parseLegendEntry(props.ges_status_legend);
  if (!carbon && !ges) return null;

  return {
    carbon_legend: carbon ?? undefined,
    ges_status_legend: ges ?? undefined,
  };
}

export function buildDefaultGesStatusLegend(): ResultsMapLegendEntry[] {
  return ([0, 1, 2, 3] as const).map((status) => {
    const meta = getGesStatusMeta(status);
    return {
      value: String(status),
      color: meta?.hexColor ?? '#868E96',
    };
  });
}

/** ClusterLayer expects Point features; polygons are converted to centroids internally. */
export function toClusterLayerData(
  data: FeatureCollection<MultiPolygon>,
): FeatureCollection<Point> {
  return data as unknown as FeatureCollection<Point>;
}

export function getLegendEntryTranslationKey(
  mode: ResultsMapColorMode,
  value: string,
): string {
  if (mode === 'carbon') {
    return `sampling.results.map.legend_scale.carbon.${value}`;
  }
  return `sampling.results.stats.ges_${value}`;
}

export function parseResultsMapGeoJson(
  data: ResultsMapGeoJson,
): ParsedResultsMapData {
  const globalFromNested = data.color_legend;

  const carbonLegend = firstLegendList(
    data.carbon_legend_scale,
    data.carbon_legend,
    globalFromNested?.carbon_legend,
  );

  const gesStatusLegend =
    firstLegendList(
      data.ges_legend_scale,
      data.ges_status_legend,
      globalFromNested?.ges_status_legend,
    ) || buildDefaultGesStatusLegend();

  const featureCollection: FeatureCollection<MultiPolygon> = {
    type: 'FeatureCollection',
    features: data.features ?? [],
  };

  return { featureCollection, carbonLegend, gesStatusLegend };
}

export function resolveFeatureMapColor(
  mode: ResultsMapColorMode,
  props: Record<string, unknown>,
): string | undefined {
  const legends = parseFeatureColorLegend(props);

  if (mode === 'carbon') {
    return (
      legends?.carbon_legend?.color ??
      (typeof props.color === 'string' ? props.color : undefined)
    );
  }

  return (
    getGesStatusHexColor(props.ges_status as GesStatusValue) ??
    legends?.ges_status_legend?.color ??
    (typeof props.color === 'string' ? props.color : undefined)
  );
}

export function applyResultsMapColors(
  data: FeatureCollection<MultiPolygon>,
  mode: ResultsMapColorMode,
): FeatureCollection<MultiPolygon> {
  return {
    ...data,
    features: data.features.map((feature) => {
      const props = (feature.properties ?? {}) as Record<string, unknown>;
      const color = resolveFeatureMapColor(mode, props);
      return {
        ...feature,
        properties: {
          ...props,
          color: color ?? props.color,
        },
      };
    }),
  };
}
